import React, { useMemo, useState, useEffect } from "react";
import { brand, Copy, modes } from "./constants";
import { Controls } from "./Controls";
import { SVGDefs } from "./SVGDefs";
import {
  exportSVG,
  exportRaster,
  copyPNGToClipboard,
  copySVGCode,
  getLatexSnippet,
} from "./exportUtils";

// Import the original large SVG rendering logic
import { SVGModeRenderer } from "./SVGModeRenderer";
// Default logo shipped with the project (if present)
// Using ?url to ensure we get a URL string at runtime
// If the file is absent, Vite will error at build; keep it in titlepage/public/logo.png
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import defaultLogo from "./public/logo.png?url";

export default function TitlepageCanvas() {
  // Copy.date removed — no runtime date displayed
  const [mode, setMode] = useState("convex");
  const [exportScale, setExportScale] = useState(2);
  const [solidBG, setSolidBG] = useState(true);
  const [showLatex, setShowLatex] = useState(false);
  const [docTitle, setDocTitle] = useState<string>(Copy.title);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [showWatermark, setShowWatermark] = useState(true);
  useEffect(() => {
    try {
      const saved = localStorage.getItem("suzan-title");
      if (saved) setDocTitle(saved);
      const savedLogo = localStorage.getItem("suzan-logo");
      if (savedLogo) setLogoDataUrl(savedLogo);
      else if (typeof defaultLogo === "string") setLogoDataUrl(defaultLogo);
    } catch {}
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem("suzan-title", docTitle);
    } catch {}
  }, [docTitle]);
  useEffect(() => {
    try {
      if (logoDataUrl) localStorage.setItem("suzan-logo", logoDataUrl);
      else localStorage.removeItem("suzan-logo");
    } catch {}
  }, [logoDataUrl]);

  // Live Copy object reflecting user edits
  const runtimeCopy = useMemo(() => ({ ...Copy, title: docTitle }), [docTitle]);

  const latexSnippet = getLatexSnippet(mode);
  
  // Get the current mode label for display
  const currentModeLabel = useMemo(() => {
    const modeEntry = modes.find(([val]) => val === mode);
    return modeEntry ? modeEntry[1] : "Geometric Mode";
  }, [mode]);

  // Initialize mode from URL (?mode= or ?model=)
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      const fromUrl = url.searchParams.get("mode") || url.searchParams.get("model");
      if (fromUrl && modes.some(([v]) => v === fromUrl)) {
        setMode(fromUrl);
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reflect current mode into URL (preserve other params)
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("mode", mode);
      window.history.replaceState({}, "", url);
    } catch {}
  }, [mode]);

  // Support browser back/forward to change mode
  useEffect(() => {
    const onPop = () => {
      try {
        const url = new URL(window.location.href);
        const m = url.searchParams.get("mode") || url.searchParams.get("model");
        if (m && modes.some(([v]) => v === m)) setMode(m);
      } catch {}
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Keyboard navigation: Arrow keys to switch modes
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault();
        const currentIndex = modes.findIndex(([val]) => val === mode);
        let nextIndex: number;
        
        if (e.key === "ArrowLeft") {
          nextIndex = currentIndex > 0 ? currentIndex - 1 : modes.length - 1;
        } else {
          nextIndex = currentIndex < modes.length - 1 ? currentIndex + 1 : 0;
        }
        
        setMode(modes[nextIndex][0]);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [mode]);

  return (
    <div className="w-full min-h-screen bg-neutral-950 flex flex-col items-center gap-4 py-6">
      {/* Controls */}
      <Controls
        mode={mode}
        exportScale={exportScale}
        solidBG={solidBG}
        showLatex={showLatex}
        modes={modes}
        onPrint={() => {
          // If local, temporarily hide watermark to avoid embedding it in local PDF
          try {
            const isLocal = typeof window !== 'undefined' && (window.location.protocol === 'file:' || window.location.hostname === 'localhost' || window.location.hostname.startsWith('127.'));
            if (isLocal) {
              setShowWatermark(false);
              // Allow the DOM to update before printing
              setTimeout(() => {
                window.print();
                setTimeout(() => setShowWatermark(true), 300);
              }, 50);
            } else {
              window.print();
            }
          } catch (e) {
            window.print();
          }
        }}
        onExportSVG={() => exportSVG(mode)}
        onExportPNG={() => exportRaster(mode, exportScale, solidBG, brand.deep, "image/png")}
        onExportWebP={() => exportRaster(mode, exportScale, solidBG, brand.deep, "image/webp")}
        onCopyPNG={() => copyPNGToClipboard(exportScale, solidBG, brand.deep)}
        onCopySVG={copySVGCode}
        onScaleChange={setExportScale}
        onSolidBGChange={setSolidBG}
        onToggleLatex={() => setShowLatex(!showLatex)}
        onModeChange={setMode}
      />

      {/* Simple form to edit the document title */}
      <form
        className="no-print w-full flex items-center justify-center"
        onSubmit={(e) => e.preventDefault()}
        aria-label="Document title form"
      >
        <label className="flex items-center gap-2 text-white/80 text-sm bg-black/30 px-3 py-2 rounded-md border border-white/20">
          <span className="opacity-80">Title:</span>
          <input
            type="text"
            value={docTitle}
            onChange={(e) => setDocTitle(e.target.value)}
            placeholder="Enter document title"
            className="bg-transparent outline-none border-none text-white placeholder-white/40 min-w-[320px]"
          />
        </label>
        <div className="ml-3 flex items-center gap-2 text-white/80 text-sm">
          <label className="flex items-center gap-2 bg-black/30 px-3 py-2 rounded-md border border-white/20 cursor-pointer">
            <span className="opacity-80">Logo (PNG/SVG):</span>
            <input
              type="file"
              accept="image/png,image/svg+xml,image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = () => {
                  const result = typeof reader.result === "string" ? reader.result : null;
                  setLogoDataUrl(result);
                };
                reader.readAsDataURL(file);
              }}
            />
            <span className="px-2 py-1 rounded bg-white/10 border border-white/10">Choose</span>
          </label>
          {logoDataUrl && (
            <button
              type="button"
              onClick={() => setLogoDataUrl(null)}
              className="px-3 py-2 rounded-md text-xs font-bold border bg-black/30 text-white"
              style={{ borderColor: "#444" }}
            >
              Remove logo
            </button>
          )}
        </div>
      </form>

      {/* Optional LaTeX snippet */}
      {showLatex && (
        <pre className="w-[794px] whitespace-pre-wrap text-xs text-green-200 bg-black/60 p-3 rounded-md border border-white/20 mb-1 no-print">
          {latexSnippet}
        </pre>
      )}

      {/* Canvas - A4 format page */}
      <div
        id="suzan-print-canvas"
        className="relative print-page"
        style={{ width: 794, height: 1123, background: mode === "grandma" ? brand.white : brand.deep }}
      >
        {/* Screen-only decorative frame (rounded + shadow) - hidden during print */}
        <div className="screen-frame no-print" aria-hidden />

        <svg id="suzan-svg" className="absolute inset-0 w-full h-full" viewBox="0 0 794 1123" xmlns="http://www.w3.org/2000/svg">
          <SVGDefs mode={mode} brand={brand} />

          {/* Optional logo embedded into SVG so it appears in exports */}
          {logoDataUrl && showWatermark && (
            <image
              href={logoDataUrl}
              x={28}
              y={28}
              width={88}
              height={88}
              opacity={0.95}
              preserveAspectRatio="xMidYMid meet"
              style={{ pointerEvents: "none" }}
            />
          )}

          {/* Render the selected artistic mode */}
          <SVGModeRenderer mode={mode} brand={brand} Copy={runtimeCopy} />

          {/* Watermark: centered semi-transparent logo for PDF export */}
          {logoDataUrl && showWatermark && (
            <image
              href={logoDataUrl}
              x={397 - 120}
              y={561.5 - 120}
              width={240}
              height={240}
              opacity={0.08}
              preserveAspectRatio="xMidYMid meet"
              style={{ pointerEvents: "none" }}
              className="print-only-watermark"
            />
          )}
        </svg>
      </div>
    </div>
  );
}
